import React, { ReactElement, useEffect } from "react";

import "../styles.scss";
import "../scripts";

import {
  KameleonElement,
  primaryCollectionBlock,
  getDefaultProps,
} from "@kameleon-core/lib";
import { Box } from "theme-ui";
import { WebflowComponentProps, webflowHOC } from "@kameleon-common/webflow-js";
import {
  backgroundColorModeMapper,
  BackgroundColors as ThemeBackgroundColors,
  Variants,
} from "@kameleon-util/global-theme";
import {
  blockConfig,
  ContentCollectionItem,
  ContentCollectionItemType,
} from "./components/content-collection-item";

import blockinfo from "./blockinfo.json";
import defaults from "./defaults.json";
import {
  ContentCollectionType as ComponentType,
  layoutOptions,
} from "./types/prop-types";
// eslint-disable-next-line import/no-extraneous-dependencies
import getEvents from "./js/webflow";

type ContentCollectionType = {
  PrimaryBlocks: ContentCollectionItemType[];
} & ComponentType;

// duration and xvalue per item
const durationTemplate = 3333.33;
const xValueTemplate = -13.5;

type PassThroughProps = {
  duration: number;
  xValue: number;
};
type CollectionWebflowType = WebflowComponentProps<
  ContentCollectionType,
  PassThroughProps
>;

const calculateItems = (
  PrimaryBlocks: ContentCollectionItemType[]
): ContentCollectionItemType[] => {
  // make sure items are not less than 8 for animation to work properly
  let duplicatedPrimaryBlocks = [...PrimaryBlocks];
  duplicatedPrimaryBlocks = duplicatedPrimaryBlocks.concat([...PrimaryBlocks]);
  while (duplicatedPrimaryBlocks.length < 8) {
    duplicatedPrimaryBlocks = duplicatedPrimaryBlocks.concat([
      ...PrimaryBlocks,
    ]);
  }
  return duplicatedPrimaryBlocks;
};

const Component = ({
  PrimaryBlocks,
  title,
  description,
  layoutProps,
  passThrough,
}: CollectionWebflowType): ReactElement => {
  const primaryBlockLayoutProps = { ...layoutProps };
  primaryBlockLayoutProps.backgroundColor = ThemeBackgroundColors.neutral;

  const duplicatedPrimaryBlocks = calculateItems(PrimaryBlocks);

  useEffect(() => {
    const updatedDuration = durationTemplate * duplicatedPrimaryBlocks.length;
    const updatedXValue = xValueTemplate * duplicatedPrimaryBlocks.length;
    passThrough?.({ xValue: updatedXValue, duration: updatedDuration });
  }, [PrimaryBlocks]);

  return (
    <Box className="primary-collection-content-collection-animated-row-small-media-v1">
      <Box
        data-w-id="659f300c-86f0-4612-17ac-4a28fca7b1b4"
        className="section_testimonial21 padding-section-large"
      >
        <Box className="padding-global container-large margin-bottom margin-xxlarge text-align-center">
          <Box className="max-width-large align-center">
            <Box className="margin-bottom margin-small">
              <Variants.Heading>{title}</Variants.Heading>
            </Box>
            <Box className="margin-bottom margin-small">
              <Variants.ParagraphMedium>{description}</Variants.ParagraphMedium>
            </Box>
          </Box>
        </Box>
        <Box className="testimonial21_component">
          {duplicatedPrimaryBlocks?.map((primaryBlockProps, i) => {
            const primaryProps = { ...primaryBlockProps };
            primaryProps.layoutProps = primaryBlockLayoutProps;
            primaryProps._parentBgColor = backgroundColorModeMapper(
              layoutProps.backgroundColor
            ).color;
            return <ContentCollectionItem key={i} {...primaryProps} />;
          })}
        </Box>
      </Box>
    </Box>
  );
};

const ComponentWithWebflow = webflowHOC<
  CollectionWebflowType,
  PassThroughProps
>(Component, [
  {
    module: "ix2",
    getEvents: (pageId, passThrough): object =>
      getEvents(pageId, passThrough?.duration, passThrough?.xValue),
  },
]);

export const ContentCollection = primaryCollectionBlock<ContentCollectionType>(
  blockConfig,
  ({ PrimaryBlocks, ...props }: ContentCollectionType) => (
    <ComponentWithWebflow PrimaryBlocks={PrimaryBlocks} {...props} />
  )
);

ContentCollection.defaultProps =
  getDefaultProps<ContentCollectionType>(defaults);

export const Element: KameleonElement = {
  component: ContentCollection,
  blockInfo: blockinfo,
  defaultProps: {
    layoutProps: defaults.layoutProps,
    contentProps: defaults.contentProps,
  },
  layoutOptions,
};
